const API_BASE = process.env.REACT_APP_API_URL || '';
const CS3_GCS_PREFIX = 'analysis/cs3s-biomech/v2';
const CS3_VIDEO_PREFIX = 'segments/wch_cs3s-2025-2026-pre-all-star';
const FT_GCS_ATTEMPTS_PATH = 'analysis/vw-fts/v1/attempts.json';

function toNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function formatClock(seconds) {
  const total = Math.max(0, Math.floor(toNumber(seconds) || 0));
  const mins = String(Math.floor(total / 60)).padStart(2, '0');
  const secs = String(total % 60).padStart(2, '0');
  return `${mins}:${secs}`;
}

function inferDistance(shotClass, shotType) {
  const normalizedType = String(shotType || '').toLowerCase();
  if (normalizedType.includes('free throw') || normalizedType.includes('ft')) return 15;
  if (shotClass === 'At Rim') return 3;
  if (shotClass === 'Paint') return 8;
  if (shotClass === '3PT') return normalizedType.includes('corner') ? 22 : 24;
  return 16;
}

function encodePathForUrl(path) {
  return String(path || '')
    .split('/')
    .map((part) => encodeURIComponent(part))
    .join('/');
}

function pickFirst(...values) {
  for (const value of values) {
    if (value !== null && value !== undefined && value !== '') return value;
  }
  return null;
}

function normalizeSteps(rawValue) {
  if (Array.isArray(rawValue)) {
    return rawValue
      .map((value) => String(value).trim())
      .filter(Boolean)
      .slice(0, 5);
  }
  if (typeof rawValue === 'string') {
    return rawValue
      .split(/\s*(?:>|->|\||,|;)\s*/)
      .map((value) => value.trim())
      .filter(Boolean)
      .slice(0, 5);
  }
  return [];
}

function inferMovementOrigin(x) {
  if (x === null) return 'Perimeter entry';
  if (x <= -14) return 'Left wing';
  if (x >= 14) return 'Right wing';
  if (Math.abs(x) <= 5) return 'Top of key';
  return x < 0 ? 'Left slot' : 'Right slot';
}

function inferFootworkStyle(shotType) {
  const normalized = String(shotType || '').toLowerCase();
  if (normalized.includes('drift')) return 'drift-step';
  if (normalized.includes('pull')) return '1-2-step';
  if (normalized.includes('hop')) return 'hop';
  if (normalized.includes('corner')) return '1-2-step';
  return '1-2-step';
}

async function fetchJsonOrNull(path) {
  try {
    const response = await fetch(`${API_BASE}${path}`);
    if (!response.ok) return null;
    return response.json();
  } catch {
    return null;
  }
}

async function fetchCs3Shots() {
  const listPayload = await fetchJsonOrNull(
    `/api/gcs-list?prefix=${encodeURIComponent(`${CS3_GCS_PREFIX}/batches/`)}`
  );

  const files = Array.isArray(listPayload?.files)
    ? listPayload.files.filter((file) => file.endsWith('.json'))
    : [];
  files.sort();

  if (files.length === 0) return [];

  const batchResults = await Promise.allSettled(
    files.map((file) => fetchJsonOrNull(`/api/gcs-proxy/${file}`))
  );

  const shots = [];
  batchResults.forEach((result) => {
    if (result.status !== 'fulfilled') return;
    const payload = result.value;
    if (Array.isArray(payload)) shots.push(...payload);
  });

  return shots;
}

async function fetchFreeThrowAttempts() {
  const gcsAttempts = await fetchJsonOrNull(`/api/gcs-proxy/${FT_GCS_ATTEMPTS_PATH}`);
  if (Array.isArray(gcsAttempts) && gcsAttempts.length > 0) return gcsAttempts;

  const localAttempts = await fetchJsonOrNull('/api/free-throw/results');
  if (Array.isArray(localAttempts?.results) && localAttempts.results.length > 0) {
    return localAttempts.results;
  }

  return [];
}

function transformCs3Shots(cs3Shots) {
  return cs3Shots
    .filter((shot) => shot && typeof shot === 'object')
    .map((shot, index) => {
      const shotType = shot.shot_type || 'Catch & Shoot';
      const shotClass = '3PT';
      const dist = toNumber(shot.distance_ft) ?? inferDistance(shotClass, shotType);
      const startSec = toNumber(shot.timestamp?.start);
      const clock = startSec !== null ? formatClock(startSec) : '--:--';
      const x = toNumber(
        shot.x ??
        shot.shot_x ??
        shot.court_x ??
        shot.loc_x ??
        shot.coordinates?.x
      );
      const z = toNumber(
        shot.z ??
        shot.shot_y ??
        shot.court_y ??
        shot.loc_y ??
        shot.coordinates?.y
      );
      const videoPath = shot.video_file
        ? `${CS3_VIDEO_PREFIX}/${String(shot.video_file).endsWith('.mp4') ? shot.video_file : `${shot.video_file}.mp4`}`
        : null;
      const movementOrigin = String(pickFirst(
        shot.movement_origin,
        shot.entry_origin,
        shot.origin_zone,
        shot.module_2_footwork?.movement_origin,
        shot.module_2_footwork?.entry_origin,
        inferMovementOrigin(x)
      ));
      const lastFiveSteps = normalizeSteps(pickFirst(
        shot.last_five_steps,
        shot.lastFiveSteps,
        shot.module_2_footwork?.last_five_steps,
        shot.module_2_footwork?.step_sequence,
        shot.module_2_footwork?.approach_steps
      ));
      const footworkStyle = String(pickFirst(
        shot.footwork_style,
        shot.module_2_footwork?.footwork_style,
        shot.module_2_footwork?.footwork_type,
        shot.module_2_footwork?.catch_footwork,
        inferFootworkStyle(shotType)
      ));
      const passOrigin = String(pickFirst(
        shot.pass_origin,
        shot.pass_source,
        shot.module_4_cognitive?.pass_origin,
        shot.module_4_cognitive?.pass_source,
        'Perimeter swing pass'
      ));

      return {
        id: shot.global_index ? `cs3_${shot.global_index}` : `cs3_${index + 1}`,
        ...(x !== null ? { x } : {}),
        ...(z !== null ? { z } : {}),
        shooter: shot.shooter || 'Unknown',
        shot_result: shot.shot_result || 'Unknown',
        shot_type: shotType,
        shotClass,
        quarter: shot.dataset ? `CS3-${shot.dataset}` : 'CS3',
        time: clock,
        dist,
        defDist: toNumber(shot.defender_distance_ft),
        ballFromHoop: dist,
        contestLevel: shot.contest_level,
        description: shot.miss_type && shot.miss_type !== 'N/A'
          ? `${shot.shooter || 'Shooter'} ${shot.shot_result || 'attempt'} (${shot.miss_type})`
          : `${shot.shooter || 'Shooter'} ${shot.shot_result || 'attempt'}`,
        videoUrl: videoPath ? `${API_BASE}/api/gcs-proxy/${encodePathForUrl(videoPath)}` : '#',
        movementOrigin,
        lastFiveSteps,
        footworkStyle,
        passOrigin,
      };
    });
}

function transformFreeThrowAttempts(attempts) {
  return attempts
    .filter((attempt) => attempt && typeof attempt === 'object')
    .map((attempt, index) => {
      const outcome = String(attempt.shot_outcome || 'unknown').toLowerCase();
      const shotClass = 'Mid-Range';
      const dist = 15;

      // Place FT shots on the line and offset by miss direction.
      let x = 0;
      if (outcome.includes('left')) x = -1.2;
      if (outcome.includes('right')) x = 1.2;
      if (outcome.includes('long')) x = 0.6;
      if (outcome.includes('short')) x = -0.6;
      x += ((index % 3) - 1) * 0.15;

      return {
        id: attempt.clip_id ? `ft_${attempt.clip_id}` : `ft_${index + 1}`,
        x,
        z: 19,
        shooter: attempt.shooter || 'Unknown',
        shot_result: outcome === 'made' ? 'Made' : 'Missed',
        result: outcome,
        type: 'Free Throw',
        shot_type: 'Free Throw',
        shotClass,
        quarter: 'FT',
        time: attempt.attempt_number ? `#${attempt.attempt_number}` : '--',
        dist,
        defDist: 8,
        ballFromHoop: dist,
        contestLevel: 'Wide Open',
        description: attempt.shot_outcome_detail || attempt.reasoning || `Free throw ${outcome}`,
        videoUrl: attempt.video_url || '#',
        movementOrigin: 'Free throw line setup',
        lastFiveSteps: ['stance', 'dip', 'rise', 'release', 'hold'],
        footworkStyle: 'set-shot',
        passOrigin: 'self-possession',
      };
    });
}

export async function fetchBiV2ProductionData() {
  const [cs3RawShots, freeThrowAttempts] = await Promise.all([
    fetchCs3Shots(),
    fetchFreeThrowAttempts(),
  ]);

  const cs3Shots = transformCs3Shots(cs3RawShots);
  const ftShots = transformFreeThrowAttempts(freeThrowAttempts);
  const shots = [...cs3Shots, ...ftShots];

  return {
    player: {
      name: 'SPURS BIv2 MATRIX',
      number: '--',
      team: 'SAN ANTONIO SPURS',
      game: '2025-26 CS3S + FREE THROWS',
    },
    shots,
    meta: {
      schemaVersion: 'biv2.0',
      source: 'production',
      cs3Shots: cs3Shots.length,
      freeThrowShots: ftShots.length,
      totalShots: shots.length,
    },
  };
}
